import { useState, useEffect } from 'react'
import api from '../services/api'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import 'react-datepicker/dist/react-datepicker.css'
import './AwsDashboardPage.css'

export default function AwsDashboardPage() {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  
  // Estado para el modal de contraseña
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')

  // Estado para navegación
  const [activeSection, setActiveSection] = useState('menu') // 'menu' o 'webhooks'

  // Estado para webhooks
  const [webhookStats, setWebhookStats] = useState(null)
  const [webhookLoading, setWebhookLoading] = useState(false)
  const [webhookError, setWebhookError] = useState(null)
  const [webhookLastUpdate, setWebhookLastUpdate] = useState(null)
  const [recentWebhooks, setRecentWebhooks] = useState([])
  const [webhookRange, setWebhookRange] = useState('24h') // Rango de tiempo seleccionado
  const [showFailedModal, setShowFailedModal] = useState(false)
  const [failedWebhooks, setFailedWebhooks] = useState([])
  const [loadingFailed, setLoadingFailed] = useState(false)

  // Estado para clientes
  const [clientsData, setClientsData] = useState(null)
  const [clientsLoading, setClientsLoading] = useState(false)
  const [clientsError, setClientsError] = useState(null)
  const [expandedClient, setExpandedClient] = useState(null)

  // Estado para estadísticas de BD
  const [dbStats, setDbStats] = useState(null)
  const [dbStatsLoading, setDbStatsLoading] = useState(false)
  const [dbStatsError, setDbStatsError] = useState(null)

  // Estado para saldo histórico
  const [balanceHistoryFromDate, setBalanceHistoryFromDate] = useState(null)
  const [balanceHistoryToDate, setBalanceHistoryToDate] = useState(null)
  const [balanceHistoryData, setBalanceHistoryData] = useState([])
  const [balanceHistoryLoading, setBalanceHistoryLoading] = useState(false)
  const [balanceHistoryError, setBalanceHistoryError] = useState('')
  const [balanceHistoryExpanded, setBalanceHistoryExpanded] = useState({})
  const [runningSnapshot, setRunningSnapshot] = useState(false)

  // Estado para carga manual de movimientos
  const [selectedClient, setSelectedClient] = useState('')
  const [manualMovement, setManualMovement] = useState({
    account_number: '',
    amount: '',
    origin: '',
    destination: '',
    description: '',
    coelsa_code: '',
    authorization_code: '',
    cvu_from: '',
    cvu_to: '',
    transfer_type: 'E' // E = Entrante, S = Saliente
  })
  const [savingMovement, setSavingMovement] = useState(false)
  const [saveResult, setSaveResult] = useState(null)

  // Opciones de rango de tiempo
  const rangeOptions = [
    { value: '24h', label: 'Últimas 24 horas' },
    { value: '7d', label: 'Últimos 7 días' },
    { value: '30d', label: 'Últimos 30 días' },
    { value: '6m', label: 'Últimos 6 meses' },
    { value: '1y', label: 'Último año' },
    { value: 'all', label: 'Todo el historial' }
  ]

  const handlePasswordSubmit = (e) => {
    e.preventDefault()
    if (password === '770') {
      setIsAuthenticated(true)
      setPasswordError('')
      // Guardar en sessionStorage para no pedir contraseña de nuevo en la sesión
      sessionStorage.setItem('aws_dashboard_auth', 'true')
    } else {
      setPasswordError('Contraseña incorrecta')
      setPassword('')
    }
  }

  // Verificar si ya está autenticado en la sesión
  useEffect(() => {
    const isAuth = sessionStorage.getItem('aws_dashboard_auth')
    if (isAuth === 'true') {
      setIsAuthenticated(true)
    }
  }, [])

  const fetchDashboard = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/aws/dashboard')
      setDashboardData(response.data)
      setLastUpdate(new Date())
    } catch (err) {
      setError(err.message || 'Error al cargar el dashboard')
    } finally {
      setLoading(false)
    }
  }

  const fetchWebhookStats = async (range = webhookRange) => {
    try {
      setWebhookLoading(true)
      setWebhookError(null)
      
      // Fetch stats y recent webhooks en paralelo con el rango seleccionado
      const [statsResponse, recentResponse] = await Promise.all([
        api.get(`/webhooks/stats?range=${range}`),
        api.get(`/webhooks/recent?limit=50&range=${range}`)
      ])
      
      setWebhookStats(statsResponse.data)
      setRecentWebhooks(recentResponse.data.webhooks || [])
      setWebhookLastUpdate(new Date())
    } catch (err) {
      setWebhookError(err.message || 'Error al cargar estadísticas de webhooks')
    } finally {
      setWebhookLoading(false)
    }
  }

  // Manejar cambio de rango
  const handleRangeChange = (newRange) => {
    setWebhookRange(newRange)
    fetchWebhookStats(newRange)
  }

  // Obtener webhooks fallidos
  const fetchFailedWebhooks = async () => {
    try {
      setLoadingFailed(true)
      const response = await api.get(`/webhooks/failed?limit=100&range=${webhookRange}`)
      setFailedWebhooks(response.data.webhooks || [])
      setShowFailedModal(true)
    } catch (err) {
      console.error('Error obteniendo webhooks fallidos:', err)
    } finally {
      setLoadingFailed(false)
    }
  }

  // Obtener información de clientes
  const fetchClientsInfo = async () => {
    try {
      setClientsLoading(true)
      setClientsError(null)
      const response = await api.get('/aws/clients-info')
      setClientsData(response.data)
    } catch (err) {
      setClientsError(err.message || 'Error al cargar clientes')
    } finally {
      setClientsLoading(false)
    }
  }

  // Toggle para expandir/colapsar cliente
  const toggleClient = (clientName) => {
    setExpandedClient(expandedClient === clientName ? null : clientName)
  }

  // Navegar a clientes desde webhooks
  const goToClients = () => {
    setActiveSection('clients')
  }

  // Obtener estadísticas de bases de datos
  const fetchDbStats = async () => {
    try {
      setDbStatsLoading(true)
      setDbStatsError(null)
      const response = await api.get('/aws/db-stats')
      setDbStats(response.data)
    } catch (err) {
      setDbStatsError(err.message || 'Error al cargar estadísticas')
    } finally {
      setDbStatsLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboard()
    // Auto-refresh cada 10 segundos para ver movimientos en tiempo real
    const interval = setInterval(fetchDashboard, 10000)
    return () => clearInterval(interval)
  }, [])

  // Cargar webhooks cuando se selecciona esa sección
  useEffect(() => {
    if (activeSection === 'webhooks' && isAuthenticated) {
      fetchWebhookStats()
    }
  }, [activeSection, isAuthenticated])

  // Cargar clientes cuando se selecciona esa sección
  useEffect(() => {
    if (activeSection === 'clients' && isAuthenticated) {
      fetchClientsInfo()
    }
  }, [activeSection, isAuthenticated])

  // Cargar estadísticas cuando se selecciona esa sección
  useEffect(() => {
    if (activeSection === 'stats' && isAuthenticated) {
      fetchDbStats()
    }
  }, [activeSection, isAuthenticated])

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
      const date = new Date(dateStr)
      return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateStr
    }
  }

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount)
  }

  // Modal de contraseña
  if (!isAuthenticated) {
    return (
      <div className="aws-dashboard">
        <div className="password-modal-overlay">
          <div className="password-modal">
            <div className="password-modal-icon">🔐</div>
            <h2>Acceso Restringido</h2>
            <p>Ingrese la contraseña para acceder al dashboard</p>
            <form onSubmit={handlePasswordSubmit}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                autoFocus
                className={passwordError ? 'error' : ''}
              />
              {passwordError && <span className="password-error">{passwordError}</span>}
              <button type="submit">Ingresar</button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  // =====================================================
  // SALDO HISTÓRICO - Funciones
  // =====================================================

  const fetchBalanceHistory = async () => {
    if (!balanceHistoryFromDate || !balanceHistoryToDate) {
      setBalanceHistoryError('Seleccione ambas fechas')
      return
    }

    setBalanceHistoryLoading(true)
    setBalanceHistoryError('')
    setBalanceHistoryData([])

    try {
      const from = format(balanceHistoryFromDate, 'yyyy-MM-dd')
      const to = format(balanceHistoryToDate, 'yyyy-MM-dd')
      
      const response = await api.get(`/accounts/balance-history?from=${from}&to=${to}`)
      
      if (response.data.success) {
        setBalanceHistoryData(response.data.data || [])
        if (response.data.data?.length === 0) {
          setBalanceHistoryError('No hay datos de saldos para el período seleccionado. Los snapshots se guardan automáticamente a las 00:00 y 12:00.')
        }
      } else {
        setBalanceHistoryError(response.data.error || 'Error al consultar el historial')
      }
    } catch (err) {
      console.error('Error:', err)
      setBalanceHistoryError(err.response?.data?.error || 'Error al consultar el historial de saldos')
    } finally {
      setBalanceHistoryLoading(false)
    }
  }

  const runManualSnapshot = async () => {
    if (runningSnapshot) return
    
    setRunningSnapshot(true)
    try {
      const response = await api.post('/accounts/balance-snapshot/run', { type: 'manual' })
      if (response.data.success) {
        alert(`✅ Snapshot ejecutado correctamente!\nProcesados: ${response.data.processed}\nErrores: ${response.data.errors || 0}`)
        // Refrescar datos si hay fechas seleccionadas
        if (balanceHistoryFromDate && balanceHistoryToDate) {
          fetchBalanceHistory()
        }
      } else {
        alert('❌ Error al ejecutar el snapshot: ' + (response.data.error || 'Error desconocido'))
      }
    } catch (err) {
      alert('❌ Error al ejecutar el snapshot: ' + (err.response?.data?.error || err.message))
    } finally {
      setRunningSnapshot(false)
    }
  }

  const toggleBalanceHistoryExpand = (key) => {
    setBalanceHistoryExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const getSnapshotTypeLabel = (type) => {
    switch (type) {
      case 'midnight': return '00:00 hs'
      case 'noon': return '12:00 hs'
      case 'manual': return 'Manual'
      default: return type
    }
  }

  const exportBalanceHistoryCSV = () => {
    if (balanceHistoryData.length === 0) return

    let csvContent = 'Fecha,Hora,Cuenta,CVU,Saldo\n'
    
    balanceHistoryData.forEach(snapshot => {
      snapshot.accounts.forEach(acc => {
        csvContent += `${snapshot.date},${getSnapshotTypeLabel(snapshot.type)},${acc.account_number},${acc.cvu || '-'},${acc.balance}\n`
      })
    })

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `saldo_historico_${format(balanceHistoryFromDate, 'yyyyMMdd')}_${format(balanceHistoryToDate, 'yyyyMMdd')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Renderizar sección de Saldo Histórico
  const renderBalanceHistorySection = () => {
    return (
      <div className="balance-history-section">
        <div className="balance-history-card">
          <div className="balance-history-header">
            <h3>📅 Consultar Historial de Saldos</h3>
            <p className="balance-history-description">
              Los saldos se guardan automáticamente a las 00:00 y 12:00 de cada día.
            </p>
          </div>

          <div className="balance-history-filters">
            <div className="filter-group">
              <label>Desde</label>
              <DatePicker
                selected={balanceHistoryFromDate}
                onChange={(date) => setBalanceHistoryFromDate(date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                placeholderText="Seleccionar fecha"
                className="date-picker-aws"
                disabled={balanceHistoryLoading}
                maxDate={new Date()}
              />
            </div>
            <div className="filter-group">
              <label>Hasta</label>
              <DatePicker
                selected={balanceHistoryToDate}
                onChange={(date) => setBalanceHistoryToDate(date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                placeholderText="Seleccionar fecha"
                className="date-picker-aws"
                disabled={balanceHistoryLoading}
                maxDate={new Date()}
                minDate={balanceHistoryFromDate}
              />
            </div>
            <button 
              className="btn-search-history"
              onClick={fetchBalanceHistory}
              disabled={balanceHistoryLoading || !balanceHistoryFromDate || !balanceHistoryToDate}
            >
              {balanceHistoryLoading ? '⏳ Consultando...' : '🔍 Consultar'}
            </button>
          </div>

          {balanceHistoryError && (
            <div className="balance-history-error">
              ⚠️ {balanceHistoryError}
            </div>
          )}

          {balanceHistoryData.length > 0 && (
            <>
              <div className="balance-history-actions">
                <button className="btn-export-csv" onClick={exportBalanceHistoryCSV}>
                  📥 Exportar CSV
                </button>
                <span className="results-count">
                  {balanceHistoryData.length} snapshot(s) encontrado(s)
                </span>
              </div>

              <div className="balance-history-results">
                {balanceHistoryData.map((snapshot, idx) => {
                  const key = `${snapshot.date}_${snapshot.type}`
                  const isExpanded = balanceHistoryExpanded[key]
                  
                  return (
                    <div key={idx} className="snapshot-card-aws">
                      <div 
                        className="snapshot-header-aws"
                        onClick={() => toggleBalanceHistoryExpand(key)}
                      >
                        <div className="snapshot-info-aws">
                          <span className="snapshot-date-aws">
                            {format(new Date(snapshot.date + 'T12:00:00'), 'dd/MM/yyyy', { locale: es })}
                          </span>
                          <span className={`snapshot-type-aws ${snapshot.type}`}>
                            {getSnapshotTypeLabel(snapshot.type)}
                          </span>
                        </div>
                        <div className="snapshot-summary-aws">
                          <span className="snapshot-accounts-aws">
                            {snapshot.accounts.length} cuenta(s)
                          </span>
                          <span className="snapshot-total-aws">
                            {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(snapshot.total)}
                          </span>
                          <span className="expand-icon-aws">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="snapshot-details-aws">
                          <table className="snapshot-table-aws">
                            <thead>
                              <tr>
                                <th>Cuenta</th>
                                <th>CVU</th>
                                <th>Saldo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {snapshot.accounts.map((acc, accIdx) => (
                                <tr key={accIdx}>
                                  <td>{acc.account_number}</td>
                                  <td className="cvu-cell">{acc.cvu || '-'}</td>
                                  <td className="amount-cell">
                                    {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(acc.balance)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  // Renderizar sección de Menu (contenido original)
  const renderMenuSection = () => {
  if (loading && !dashboardData) {
    return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Conectando con movements-db...</p>
      </div>
    )
  }

  return (
      <>
      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      {lastUpdate && (
        <div className="last-update">
          Última actualización: {formatDate(lastUpdate)}
        </div>
      )}

      {/* Stats generales */}
      <div className="general-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{dashboardData?.totalClients || 0}</div>
            <div className="stat-label">Clientes</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{dashboardData?.totalMovements?.toLocaleString() || 0}</div>
            <div className="stat-label">Movimientos Totales</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💾</div>
          <div className="stat-content">
            <div className="stat-value">10.0.158.166</div>
            <div className="stat-label">IP Instancia</div>
          </div>
        </div>
      </div>

      {/* Cards de clientes */}
      <div className="clients-section">
        <h2>📁 Bases de Datos por Cliente</h2>
        <div className="clients-grid">
          {dashboardData?.clients?.map((client) => (
            <div key={client.name} className="client-card">
              <div className="client-header">
                <h3>{client.name}</h3>
                <span className="db-name">{client.name}.db</span>
              </div>
              
              {client.stats ? (
                <>
                  <div className="client-stats">
                    <div className="client-stat">
                      <span className="label">Movimientos:</span>
                      <span className="value">{client.stats.total_movements?.toLocaleString() || 0}</span>
                    </div>
                    <div className="client-stat">
                      <span className="label">Cuentas:</span>
                      <span className="value">{client.stats.accounts || 0}</span>
                    </div>
                    <div className="client-stat income">
                      <span className="label">Ingresos:</span>
                      <span className="value">{formatCurrency(client.stats.total_income)}</span>
                    </div>
                    <div className="client-stat expense">
                      <span className="label">Egresos:</span>
                      <span className="value">{formatCurrency(client.stats.total_expenses)}</span>
                    </div>
                    <div className="client-stat">
                      <span className="label">Primer mov:</span>
                      <span className="value">{formatDate(client.stats.first_movement)}</span>
                    </div>
                    <div className="client-stat">
                      <span className="label">Último mov:</span>
                      <span className="value">{formatDate(client.stats.last_movement)}</span>
                    </div>
                  </div>

                  {/* Últimos 10 movimientos */}
                  <div className="movements-section">
                    <h4>📋 Últimos 10 Movimientos</h4>
                    {client.lastMovements?.length > 0 ? (
                      <div className="movements-table-container">
                        <table className="movements-table">
                          <thead>
                            <tr>
                              <th>Fecha/Hora</th>
                              <th>Descripción</th>
                              <th>Origen</th>
                              <th>Destino</th>
                              <th>Monto</th>
                              <th>COELSA</th>
                            </tr>
                          </thead>
                          <tbody>
                            {client.lastMovements.map((mov, idx) => (
                              <tr key={mov.id || idx}>
                                <td>{formatDate(mov.timestamp)}</td>
                                <td className="description">{mov.description || '-'}</td>
                                <td>{mov.origin || '-'}</td>
                                <td>{mov.destination || '-'}</td>
                                <td className={parseFloat(mov.amount) >= 0 ? 'amount positive' : 'amount negative'}>
                                  {formatCurrency(mov.amount)}
                                </td>
                                <td className="coelsa">{mov.coelsa_code || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-movements">Sin movimientos registrados</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="client-error">
                  ⚠️ Error al cargar datos
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      </>
    )
  }

  // Renderizar sección de Webhooks
  const renderWebhooksSection = () => {
    if (webhookLoading && !webhookStats) {
      return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando estadísticas de webhooks...</p>
        </div>
      )
    }

    return (
      <>
        {webhookError && (
          <div className="error-banner">
            ⚠️ {webhookError}
          </div>
        )}

        <div className="webhook-header-controls">
          <div className="range-selector">
            <label>📅 Período:</label>
            <select 
              value={webhookRange} 
              onChange={(e) => handleRangeChange(e.target.value)}
              disabled={webhookLoading}
            >
              {rangeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="controls-right">
            {webhookLastUpdate && (
              <div className="last-update">
                Última actualización: {formatDate(webhookLastUpdate)}
              </div>
            )}
            <button 
              className="refresh-btn webhook-refresh" 
              onClick={() => fetchWebhookStats(webhookRange)} 
              disabled={webhookLoading}
            >
              {webhookLoading ? '⏳' : '🔄'} Actualizar
            </button>
          </div>
        </div>

        {/* Status del sistema */}
        <div className="webhook-system-status">
          <div className={`status-badge ${webhookStats?.webhook_to_db_enabled ? 'active' : 'inactive'}`}>
            <span className="status-indicator"></span>
            {webhookStats?.webhook_to_db_enabled ? 'Sistema Activo' : 'Sistema Inactivo'}
          </div>
        </div>

        {/* Período seleccionado */}
        <div className="range-display">
          📊 Mostrando: <strong>{webhookStats?.range_label || 'Últimas 24 horas'}</strong>
        </div>

        {/* Stats de webhooks */}
        <div className="webhook-stats-grid">
          <div className="webhook-stat-card total">
            <div className="webhook-stat-icon">📥</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.total || 0}</div>
            <div className="webhook-stat-label">Total Recibidos</div>
          </div>
          <div className="webhook-stat-card saved">
            <div className="webhook-stat-icon">✅</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.saved || 0}</div>
            <div className="webhook-stat-label">Guardados</div>
          </div>
          <div 
            className={`webhook-stat-card failed ${(webhookStats?.stats?.failed || 0) > 0 ? 'clickable' : ''}`}
            onClick={() => (webhookStats?.stats?.failed || 0) > 0 && fetchFailedWebhooks()}
            title={(webhookStats?.stats?.failed || 0) > 0 ? 'Click para ver detalles' : ''}
          >
            <div className="webhook-stat-icon">❌</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.failed || 0}</div>
            <div className="webhook-stat-label">Fallidos {(webhookStats?.stats?.failed || 0) > 0 && '(ver)'}</div>
          </div>
          <div className="webhook-stat-card clients">
            <div className="webhook-stat-icon">👥</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.clients || 0}</div>
            <div className="webhook-stat-label">Clientes</div>
          </div>
          <div className="webhook-stat-card incoming">
            <div className="webhook-stat-icon">📩</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.entrantes || 0}</div>
            <div className="webhook-stat-label">Entrantes</div>
          </div>
          <div className="webhook-stat-card outgoing">
            <div className="webhook-stat-icon">📤</div>
            <div className="webhook-stat-value">{webhookStats?.stats?.salientes || 0}</div>
            <div className="webhook-stat-label">Salientes</div>
          </div>
        </div>

        {/* Conexiones SSE */}
        <div className="sse-connections">
          <span className="sse-icon">🔗</span>
          <span className="sse-count">{webhookStats?.sse_clients_connected || 0}</span>
          <span className="sse-label">clientes conectados en tiempo real</span>
        </div>

        {/* Últimos webhooks */}
        <div className="recent-webhooks-section">
          <h3>📋 Últimos Webhooks Recibidos</h3>
          {recentWebhooks.length > 0 ? (
            <div className="webhooks-table-container">
              <table className="webhooks-table">
                <thead>
                  <tr>
                    <th>Hora</th>
                    <th>Tipo</th>
                    <th>Cuenta</th>
                    <th>De</th>
                    <th>Monto</th>
                    <th>COELSA ID</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {recentWebhooks.map((wh, idx) => (
                    <tr key={idx}>
                      <td className="time-cell">{wh.received_at?.split(' ')[1] || '-'}</td>
                      <td>
                        <span className={`type-badge ${wh.transfer_type === 'ENTRANTE' ? 'incoming' : 'outgoing'}`}>
                          {wh.transfer_type === 'ENTRANTE' ? '📩' : '📤'} {wh.transfer_type}
                        </span>
                      </td>
                      <td className="account-cell">{wh.account || '-'}</td>
                      <td className="from-cell">{wh.from || '-'}</td>
                      <td className="amount-cell">{formatCurrency(wh.amount)}</td>
                      <td className="coelsa-cell">{wh.coelsa_id || '-'}</td>
                      <td className="client-cell">{wh.client || '-'}</td>
                      <td>
                        {wh.saved ? (
                          <span className="status-saved">✅</span>
                        ) : (
                          <span className="status-failed" title={wh.error}>❌</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-webhooks">No hay webhooks recientes</p>
          )}
        </div>
      </>
    )
  }

  // Renderizar sección de Clientes
  const renderClientsSection = () => {
    if (clientsLoading && !clientsData) {
      return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando información de clientes...</p>
        </div>
      )
    }

    return (
      <>
        {clientsError && (
          <div className="error-banner">
            ⚠️ {clientsError}
          </div>
        )}

        <div className="clients-info-header">
          <div className="clients-summary-inline">
            <span><strong>{clientsData?.totalClients || 0}</strong> clientes</span>
            <span><strong>{clientsData?.totalAccounts || 0}</strong> cuentas</span>
            <span><strong>{clientsData?.totalCVUs || 0}</strong> CVUs</span>
          </div>
          <button 
            className="refresh-btn" 
            onClick={fetchClientsInfo} 
            disabled={clientsLoading}
          >
            {clientsLoading ? '⏳' : '🔄'} Actualizar
          </button>
        </div>

        {/* Grid de clientes - 3 por fila */}
        <div className="clients-grid">
          {clientsData?.clients?.map((client) => (
            <div key={client.name} className="client-card">
              <div className="client-card-header">
                <span className="client-card-name">{client.name}</span>
                <span className="client-card-count">{client.accountCount}</span>
              </div>
              
              <div className="client-card-body">
                <div className="client-card-column">
                  <div className="column-title">CVUs</div>
                  <div className="column-list">
                    {client.cvus?.map((cvu, idx) => (
                      <div key={idx} className="list-item cvu">{cvu}</div>
                    ))}
                  </div>
                </div>
                
                <div className="client-card-column">
                  <div className="column-title">Cuentas</div>
                  <div className="column-list">
                    {client.accounts?.map((acc, idx) => (
                      <div key={idx} className="list-item account">{acc}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    )
  }

  // Renderizar sección de Estadísticas
  const renderStatsSection = () => {
    if (dbStatsLoading && !dbStats) {
      return (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando estadísticas de almacenamiento...</p>
        </div>
      )
    }

    // Función para obtener color según porcentaje
    const getProgressColor = (percent) => {
      if (percent < 25) return '#00ff88'
      if (percent < 50) return '#00d9ff'
      if (percent < 75) return '#ffaa00'
      return '#ff4444'
    }

    return (
      <>
        {dbStatsError && (
          <div className="error-banner">
            ⚠️ {dbStatsError}
          </div>
        )}

        <div className="stats-header">
          <div className="stats-summary">
            <span><strong>{dbStats?.totalDatabases || 0}</strong> bases de datos</span>
            <span><strong>{(dbStats?.totalMovements || 0).toLocaleString()}</strong> movimientos</span>
            <span><strong>{dbStats?.totalSizeMB || 0}</strong> MB estimados</span>
          </div>
          <button 
            className="refresh-btn" 
            onClick={fetchDbStats} 
            disabled={dbStatsLoading}
          >
            {dbStatsLoading ? '⏳' : '🔄'} Actualizar
          </button>
        </div>

        <div className="db-stats-grid">
          {dbStats?.databases?.map((db) => (
            <div key={db.name} className="db-stat-card">
              <div className="db-stat-header">
                <span className="db-name">🗄️ {db.name}</span>
                <span className="db-size">{db.sizeMB} MB</span>
              </div>
              
              <div className="db-movements-info">
                <span className="db-movements">{(db.totalMovements || 0).toLocaleString()} movimientos</span>
                <span className="db-accounts">{db.accounts || 0} cuentas</span>
              </div>
              
              <div className="db-progress-container">
                <div className="db-progress-bar">
                  <div 
                    className="db-progress-fill"
                    style={{ 
                      width: `${Math.max(Math.min(db.percentUsed, 100), 0.5)}%`,
                      backgroundColor: getProgressColor(db.percentUsed)
                    }}
                  />
                </div>
                <span className="db-percent" style={{ color: getProgressColor(db.percentUsed) }}>
                  {db.percentUsed < 0.01 ? '<0.01' : db.percentUsed}%
                </span>
              </div>
              
              <div className="db-dates-info">
                {db.firstMovement && (
                  <div className="db-date-row">
                    <span className="date-label">Primer mov:</span>
                    <span className="date-value">{new Date(db.firstMovement).toLocaleDateString('es-AR')}</span>
                  </div>
                )}
                {db.lastMovement && (
                  <div className="db-date-row">
                    <span className="date-label">Último mov:</span>
                    <span className="date-value">{new Date(db.lastMovement).toLocaleDateString('es-AR')}</span>
                  </div>
                )}
                {db.firstMovement && (
                  <div className="db-date-row">
                    <span className="date-label">Días activo:</span>
                    <span className="date-value days-badge">
                      {Math.ceil((new Date() - new Date(db.firstMovement)) / (1000 * 60 * 60 * 24))} días
                    </span>
                  </div>
                )}
              </div>
              
              <div className="db-stat-footer">
                <span className="db-max">Máx: {db.maxSizeGB} GB</span>
              </div>
            </div>
          ))}
        </div>

        <div className="stats-note">
          <p>💡 <strong>Nota:</strong> Los tamaños son estimados (~500 bytes/movimiento). SQLite soporta hasta ~140 TB. Límite configurado: 10 GB/BD.</p>
        </div>
      </>
    )
  }

  // Lista de clientes disponibles para carga manual
  const availableClients = [
    { value: 'joaquin', label: 'Joaquin' },
    { value: 'marturecauda', label: 'Marturecauda' },
    { value: 'delta', label: 'Delta' },
    { value: 'yugo', label: 'Yugo' },
    { value: 'ND_', label: 'ND_' },
    { value: 'aritos', label: 'Aritos' }
  ]

  const handleManualMovementChange = (field, value) => {
    setManualMovement(prev => ({ ...prev, [field]: value }))
  }

  const handleSaveManualMovement = async () => {
    if (!selectedClient) {
      setSaveResult({ success: false, message: 'Selecciona un cliente' })
      return
    }
    if (!manualMovement.account_number || !manualMovement.amount) {
      setSaveResult({ success: false, message: 'Número de cuenta y monto son obligatorios' })
      return
    }

    setSavingMovement(true)
    setSaveResult(null)

    try {
      // Generar timestamp actual en Argentina
      const now = new Date()
      const argTime = new Date(now.getTime() - (3 * 60 * 60 * 1000))
      const timestamp = argTime.toISOString().replace('T', ' ').substring(0, 19)

      const movementData = {
        ...manualMovement,
        timestamp,
        amount: parseFloat(manualMovement.amount.replace(/\./g, '').replace(',', '.')) || 0,
        movement_id: `MANUAL_${Date.now()}`,
        tx_type: manualMovement.transfer_type === 'E' ? 3501 : 3500
      }

      const response = await api.post(`/aws/movements/${selectedClient}`, movementData)
      
      if (response.data.success) {
        setSaveResult({ success: true, message: '✅ Movimiento guardado exitosamente' })
        // Limpiar formulario
        setManualMovement({
          account_number: '',
          amount: '',
          origin: '',
          destination: '',
          description: '',
          coelsa_code: '',
          authorization_code: '',
          cvu_from: '',
          cvu_to: '',
          transfer_type: 'E'
        })
      } else {
        setSaveResult({ success: false, message: response.data.error || 'Error al guardar' })
      }
    } catch (err) {
      setSaveResult({ success: false, message: err.response?.data?.error || err.message })
    } finally {
      setSavingMovement(false)
    }
  }

  // Renderizar sección de carga manual
  const renderUploadSection = () => {
    return (
      <div className="upload-section">
        <div className="upload-card">
          <h3>📤 Cargar Movimiento Manual</h3>
          
          {/* Selector de cliente */}
          <div className="upload-field">
            <label>Cliente (Base de datos) *</label>
            <select 
              value={selectedClient} 
              onChange={(e) => setSelectedClient(e.target.value)}
              className="upload-select"
            >
              <option value="">-- Seleccionar cliente --</option>
              {availableClients.map(client => (
                <option key={client.value} value={client.value}>{client.label}</option>
              ))}
            </select>
          </div>

          {selectedClient && (
            <div className="upload-form">
              <div className="upload-form-grid">
                {/* Tipo de transferencia */}
                <div className="upload-field">
                  <label>Tipo de Transferencia *</label>
                  <select
                    value={manualMovement.transfer_type}
                    onChange={(e) => handleManualMovementChange('transfer_type', e.target.value)}
                    className="upload-select"
                  >
                    <option value="E">Entrante (Ingreso)</option>
                    <option value="S">Saliente (Egreso)</option>
                  </select>
                </div>

                {/* Número de cuenta */}
                <div className="upload-field">
                  <label>Número de Cuenta *</label>
                  <input
                    type="text"
                    value={manualMovement.account_number}
                    onChange={(e) => handleManualMovementChange('account_number', e.target.value)}
                    placeholder="Ej: 564733753"
                    className="upload-input"
                  />
                </div>

                {/* Monto */}
                <div className="upload-field">
                  <label>Monto *</label>
                  <input
                    type="text"
                    value={manualMovement.amount}
                    onChange={(e) => handleManualMovementChange('amount', e.target.value)}
                    placeholder="Ej: 32.000,00"
                    className="upload-input"
                  />
                </div>

                {/* Origen */}
                <div className="upload-field">
                  <label>Origen (Nombre titular)</label>
                  <input
                    type="text"
                    value={manualMovement.origin}
                    onChange={(e) => handleManualMovementChange('origin', e.target.value)}
                    placeholder="Ej: Juan Perez"
                    className="upload-input"
                  />
                </div>

                {/* Destino */}
                <div className="upload-field">
                  <label>Destino (Nombre titular)</label>
                  <input
                    type="text"
                    value={manualMovement.destination}
                    onChange={(e) => handleManualMovementChange('destination', e.target.value)}
                    placeholder="Ej: CLEANPOWER SOLUTIONS SRL"
                    className="upload-input"
                  />
                </div>

                {/* Descripción */}
                <div className="upload-field">
                  <label>Descripción</label>
                  <input
                    type="text"
                    value={manualMovement.description}
                    onChange={(e) => handleManualMovementChange('description', e.target.value)}
                    placeholder="Ej: Transferencia recibida"
                    className="upload-input"
                  />
                </div>

                {/* CVU Origen */}
                <div className="upload-field">
                  <label>CVU Origen</label>
                  <input
                    type="text"
                    value={manualMovement.cvu_from}
                    onChange={(e) => handleManualMovementChange('cvu_from', e.target.value)}
                    placeholder="22 dígitos"
                    className="upload-input"
                  />
                </div>

                {/* CVU Destino */}
                <div className="upload-field">
                  <label>CVU Destino</label>
                  <input
                    type="text"
                    value={manualMovement.cvu_to}
                    onChange={(e) => handleManualMovementChange('cvu_to', e.target.value)}
                    placeholder="22 dígitos"
                    className="upload-input"
                  />
                </div>

                {/* COELSA ID */}
                <div className="upload-field">
                  <label>COELSA ID</label>
                  <input
                    type="text"
                    value={manualMovement.coelsa_code}
                    onChange={(e) => handleManualMovementChange('coelsa_code', e.target.value)}
                    placeholder="Código COELSA"
                    className="upload-input"
                  />
                </div>

                {/* Código de autorización */}
                <div className="upload-field">
                  <label>Código de Autorización</label>
                  <input
                    type="text"
                    value={manualMovement.authorization_code}
                    onChange={(e) => handleManualMovementChange('authorization_code', e.target.value)}
                    placeholder="6 dígitos"
                    className="upload-input"
                  />
                </div>
              </div>

              {/* Resultado */}
              {saveResult && (
                <div className={`save-result ${saveResult.success ? 'success' : 'error'}`}>
                  {saveResult.message}
                </div>
              )}

              {/* Botón guardar */}
              <div className="upload-actions">
                <button 
                  className="upload-btn"
                  onClick={handleSaveManualMovement}
                  disabled={savingMovement}
                >
                  {savingMovement ? '⏳ Guardando...' : '💾 Guardar Movimiento'}
                </button>
              </div>

              <div className="upload-note">
                <p>💡 <strong>Nota:</strong> El timestamp se generará automáticamente con la hora actual de Argentina.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="aws-dashboard">
      {/* Navbar */}
      <nav className="aws-navbar">
        <div className="navbar-brand">
          <span className="brand-icon">☁️</span>
          <span className="brand-text">AWS Dashboard</span>
        </div>
        <div className="navbar-menu">
          <button 
            className={`nav-item ${activeSection === 'menu' ? 'active' : ''}`}
            onClick={() => setActiveSection('menu')}
          >
            <span className="nav-icon">🗄️</span>
            <span className="nav-text">Menu</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'webhooks' ? 'active' : ''}`}
            onClick={() => setActiveSection('webhooks')}
          >
            <span className="nav-icon">🔔</span>
            <span className="nav-text">Webhooks</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveSection('clients')}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Clientes</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveSection('stats')}
          >
            <span className="nav-icon">📊</span>
            <span className="nav-text">Estadísticas</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveSection('upload')}
          >
            <span className="nav-icon">📤</span>
            <span className="nav-text">Cargar Movimientos</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'balance-history' ? 'active' : ''}`}
            onClick={() => setActiveSection('balance-history')}
          >
            <span className="nav-icon">📜</span>
            <span className="nav-text">Saldo Histórico</span>
          </button>
        </div>
        <div className="navbar-status">
          <div className={`service-status ${dashboardData?.service === 'online' ? 'online' : 'offline'}`}>
            <span className="status-dot"></span>
            {dashboardData?.service === 'online' ? 'Online' : 'Offline'}
          </div>
        </div>
      </nav>

      {/* Header de sección */}
      <header className="section-header">
        <div className="header-left">
          {activeSection === 'menu' && (
            <>
              <h1>🗄️ AWS Movements Database</h1>
              <p className="subtitle">Monitoreo de bases de datos SQLite</p>
            </>
          )}
          {activeSection === 'webhooks' && (
            <>
              <h1>🔔 Webhook Movements</h1>
              <p className="subtitle">Estadísticas de webhooks</p>
            </>
          )}
          {activeSection === 'clients' && (
            <>
              <h1>👥 Clientes Configurados</h1>
              <p className="subtitle">Cuentas y CVUs asignados por cliente</p>
            </>
          )}
          {activeSection === 'stats' && (
            <>
              <h1>📊 Estadísticas de Almacenamiento</h1>
              <p className="subtitle">Uso de espacio en bases de datos</p>
            </>
          )}
          {activeSection === 'upload' && (
            <>
              <h1>📤 Cargar Movimientos</h1>
              <p className="subtitle">Carga manual de movimientos a la base de datos</p>
            </>
          )}
          {activeSection === 'balance-history' && (
            <>
              <h1>📜 Saldo Histórico</h1>
              <p className="subtitle">Historial de saldos guardados cada 12 horas</p>
            </>
          )}
        </div>
        {activeSection === 'menu' && (
          <div className="header-right">
            <button className="refresh-btn" onClick={fetchDashboard} disabled={loading}>
              {loading ? '⏳' : '🔄'} Actualizar
            </button>
          </div>
        )}
      </header>

      {/* Contenido */}
      <main className="dashboard-content">
        {activeSection === 'menu' && renderMenuSection()}
        {activeSection === 'webhooks' && renderWebhooksSection()}
        {activeSection === 'clients' && renderClientsSection()}
        {activeSection === 'stats' && renderStatsSection()}
        {activeSection === 'upload' && renderUploadSection()}
        {activeSection === 'balance-history' && renderBalanceHistorySection()}
      </main>

      {/* Modal de Webhooks Fallidos */}
      {showFailedModal && (
        <div className="failed-modal-overlay" onClick={() => setShowFailedModal(false)}>
          <div className="failed-modal" onClick={(e) => e.stopPropagation()}>
            <div className="failed-modal-header">
              <h2>❌ Webhooks Fallidos</h2>
              <button className="close-modal" onClick={() => setShowFailedModal(false)}>✕</button>
            </div>
            
            {loadingFailed ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando...</p>
              </div>
            ) : failedWebhooks.length > 0 ? (
              <div className="failed-modal-content">
                <p className="failed-count">
                  Se encontraron <strong>{failedWebhooks.length}</strong> webhooks fallidos en el período seleccionado
                </p>
                <div className="failed-table-container">
                  <table className="failed-table">
                    <thead>
                      <tr>
                        <th>Fecha/Hora</th>
                        <th>Cuenta</th>
                        <th>De</th>
                        <th>Monto</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedWebhooks.map((wh, idx) => (
                        <tr key={idx}>
                          <td className="time-cell">{wh.received_at || '-'}</td>
                          <td className="account-cell">{wh.account || '-'}</td>
                          <td className="from-cell">{wh.from || '-'}</td>
                          <td className="amount-cell">{formatCurrency(wh.amount)}</td>
                          <td className="error-cell">
                            {wh.error}
                            {wh.error?.includes('Sin base de datos') && (
                              <button 
                                className="btn-go-clients"
                                onClick={() => { setShowFailedModal(false); goToClients(); }}
                              >
                                → Ver Clientes
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="failed-modal-footer">
                  <p>💡 Si ves errores de "Sin base de datos asignada", podés ir a <button className="link-btn" onClick={() => { setShowFailedModal(false); goToClients(); }}>👥 Clientes</button> para ver las cuentas configuradas.</p>
                </div>
              </div>
            ) : (
              <div className="no-failed">
                <p>✅ No hay webhooks fallidos en este período</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
